/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Smart city/location search field shared by the main search and the tenant storefront.
 * - free text with substring suggestions over the served cities;
 * - Claude (Haiku) smart-resolve of landmarks/airports/boroughs on submit (api.resolveLocation);
 * - VOICE search via the browser Web Speech API in the active site language (hard 10s cap so it
 *   never waits longer than ~10 seconds for a result), the transcript then goes through the same
 *   smart-resolve. Exposes submit() so a parent form/button can trigger the resolve+search.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { MapPin, Mic, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

export interface CitySearchHandle {
  submit: () => void;
}

interface Props {
  cities: string[];
  /** Called with the final city to search ('' = all cities). The parent runs the actual car search. */
  onSearch: (city: string) => void;
}

// Site language → Web Speech BCP-47 locale.
const LANG_TO_BCP47: Record<string, string> = {
  en: 'en-US', es: 'es-ES', pt: 'pt-PT', fr: 'fr-FR', de: 'de-DE',
  it: 'it-IT', ru: 'ru-RU', 'zh-TW': 'zh-TW', 'zh-CN': 'zh-CN',
};

const CitySearchInput = forwardRef<CitySearchHandle, Props>(({ cities, onSearch }, ref) => {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [listening, setListening] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const recRef = useRef<any>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const q = text.trim();
  const suggestions = q ? cities.filter((c) => c.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];

  const resolveAndSearch = async (raw?: string) => {
    const query = (raw ?? text).trim();
    if (!query) { onSearch(''); return; }
    const exact = cities.find((c) => c.toLowerCase() === query.toLowerCase());
    if (exact) { setText(exact); onSearch(exact); return; }
    setResolving(true);
    try {
      const r = await api.resolveLocation(query);
      if (r) { setText(r); onSearch(r); }
      else onSearch(query); // nothing matched → empty results
    } finally {
      setResolving(false);
    }
  };

  useImperativeHandle(ref, () => ({ submit: () => { setShowSuggest(false); resolveAndSearch(); } }));

  const pick = (c: string) => { setText(c); setShowSuggest(false); onSearch(c); };

  const SR: any = typeof window !== 'undefined'
    ? ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)
    : null;

  const startVoice = () => {
    if (!SR) return;
    if (listening) { try { recRef.current?.stop(); } catch { /* noop */ } setListening(false); return; }
    try {
      const rec = new SR();
      rec.lang = LANG_TO_BCP47[i18n.language] || 'en-US';
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e: any) => {
        const tr = e?.results?.[0]?.[0]?.transcript ?? '';
        if (tr) { setText(tr); resolveAndSearch(tr); }
      };
      rec.onerror = () => setListening(false);
      rec.onend = () => setListening(false);
      recRef.current = rec;
      setListening(true);
      rec.start();
      // Hard cap: never keep the mic open / wait longer than ~10 seconds.
      setTimeout(() => { try { rec.stop(); } catch { /* noop */ } }, 10000);
    } catch {
      setListening(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={text}
          onChange={(e) => { setText(e.target.value); setShowSuggest(true); }}
          onFocus={() => setShowSuggest(true)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setShowSuggest(false); resolveAndSearch(); } }}
          placeholder={t('search.cityPlaceholder')}
          className="w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          autoComplete="off"
        />
        {(!!SR || resolving) && (
          <button
            type="button"
            onClick={startVoice}
            disabled={resolving}
            title={t('search.voice')}
            aria-label={t('search.voice')}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-colors ${
              listening ? 'bg-red-500 text-white animate-pulse' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {resolving ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
          </button>
        )}
      </div>

      {showSuggest && suggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => pick(c)}
              className="w-full text-left px-4 py-2 text-gray-900 hover:bg-gray-100 flex items-center gap-2"
            >
              <MapPin size={14} className="text-gray-400 shrink-0" /> {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

CitySearchInput.displayName = 'CitySearchInput';
export default CitySearchInput;
